/**
 * THE ESTATE — every machine we run, and the repository each one came from.
 *
 * Erez asked for one picture of the whole company (2026-08-12): "all the boxes
 * and what's inside of them, and also the repository." The board already has a
 * platform view and a per-client view; what was missing is the view ACROSS
 * clients, where the differences between them are the subject.
 *
 * A DEPLOYMENT view, per the skill — so ports are part of the name. Position
 * carries the argument: the repositories sit BETWEEN the two clients that share
 * them, the machines fan out either side, and there is a wire for every one that
 * can trace its code back to a repository. The wires that are missing are the
 * finding. Air Duct has none at all; every client's front end arrives by rsync
 * from a laptop rather than from a repo.
 *
 * Everything here was read off the running machines — services, containers,
 * ports and git remotes. Falcon, Outform and Air Duct on 12 Aug 2026 over SSH;
 * BOILING on 13 Aug via AWS SSM, once Erez pointed at the keys in
 * integrations/boilingoffers-aws. It had been drawn hollow and marked NOT
 * VERIFIED rather than guessed at, and filling it in changed the conclusion:
 * Boiling repeats the pattern exactly — `boiling-web` is a bundle with no
 * repository, the fourth of four.
 *
 * One correction is baked into the picture: `tailormind-console` carries the
 * front end for Falcon and Outform ONLY. Boiling's console source is three
 * UNTRACKED files in one clone — files sitting in a working folder are not
 * repository contents, which is exactly the confusion this diagram exists to
 * end.
 */
import { Canvas, leaf, box, type Diagram } from "./c4";

const ESTATE: Diagram = (() => {
  // ── What we own, on GitHub ─────────────────────────────────────────────────
  // The console is drawn first and alone because it is the ONLY thing any two
  // clients share. Everything under it is one repository per client.
  const repos = box("es-repos", "GITHUB", "col", [
    box("es-shared", "SHARED BY TWO CLIENTS", "col", [
      leaf("es-console", "tailormind-console", "[fork of omnigent]", "GitFork"),
      box("es-tenants", undefined, "row", [
        leaf("es-t-fal", "web/src/falcon", undefined, "Folder"),
        leaf("es-t-out", "web/src/outform", undefined, "Folder"),
      ], { pad: 0, gap: 10 }),
    ], { gap: 10 }),
    box("es-backends", "ONE PER CLIENT", "col", [
      leaf("es-r-fal", "tailormind-falcon", undefined, "GitBranch"),
      leaf("es-r-out", "tailormind-outform", undefined, "GitBranch"),
      leaf("es-r-boi", "tailormind-boiling", undefined, "GitBranch"),
      leaf("es-r-air", "Air Duct — none", "[no repository]", "GitBranch", "gate"),
    ], { gap: 10 }),
  ], { gap: 16 });

  // ── Falcon ─────────────────────────────────────────────────────────────────
  // The fullest build: three host daemons, voice and Telegram. Every other
  // client is this minus something, which is why it is drawn first.
  const falcon = box("es-falcon", "FALCON · Hetzner", "col", [
    box("es-f-vendor", "omnigent [vendor]", "col", [
      leaf("es-f-caddy", "caddy :443", undefined, "Network"),
      leaf("es-f-omni", "omnigent :8000", "[pinned @sha256]", "Boxes"),
      leaf("es-f-pg", "postgres :5432", undefined, "Database"),
      leaf("es-f-host", "host daemons ×3", "[dan · erez · root]", "Cpu"),
    ], { gap: 9 }),
    box("es-f-ours", "ours", "col", [
      leaf("es-f-app", "falcon-app :8090", "[API + MCP]", "Server"),
      leaf("es-f-voice", "falcon-voice :8140", "[Hebrew]", "Mic"),
      leaf("es-f-tg", "omni-falcon", "[Telegram]", "Send"),
    ], { gap: 9 }),
    leaf("es-f-web", "falcon-web", "[NO REPO]", "LayoutDashboard", "gate"),
    leaf("es-f-db", "falcon-db :5432", "[pgvector]", "Database"),
  ], { gap: 12 });

  // ── Outform ────────────────────────────────────────────────────────────────
  const outform = box("es-outform", "OUTFORM · Hetzner", "col", [
    box("es-o-vendor", "omnigent [vendor]", "col", [
      leaf("es-o-caddy", "caddy :443", undefined, "Network"),
      leaf("es-o-omni", "omnigent :8000", "[pinned @sha256]", "Boxes"),
      leaf("es-o-pg", "postgres :5432", undefined, "Database"),
      leaf("es-o-host", "host daemon ×1", undefined, "Cpu"),
    ], { gap: 9 }),
    box("es-o-ours", "ours", "col", [
      leaf("es-o-app", "outform-app", "[API + MCP]", "Server"),
    ], { gap: 9 }),
    leaf("es-o-web", "outform-web", "[NO REPO]", "LayoutDashboard", "gate"),
    leaf("es-o-db", "outform-db :5432", "[pgvector]", "Database"),
  ], { gap: 12 });

  // ── Air Duct ───────────────────────────────────────────────────────────────
  // Different three ways, and every difference is drawn: the vendor image is
  // PULLED rather than built, our app runs as a container rather than a
  // service, and nothing here traces back to a repository at all.
  const airduct = box("es-airduct", "AIR DUCT · Hetzner", "col", [
    box("es-a-vendor", "omnigent [vendor]", "col", [
      leaf("es-a-caddy", "airduct-caddy", "[its own]", "Network"),
      leaf("es-a-omni", "omnigent", "[:latest \u2014 floats]", "Boxes", "gate"),
      leaf("es-a-pg", "postgres :5432", undefined, "Database"),
      leaf("es-a-host", "host daemon ×1", undefined, "Cpu"),
    ], { gap: 9 }),
    box("es-a-ours", "ours", "col", [
      leaf("es-a-app", "airduct-app", "[docker · NO REPO]", "Server", "gate"),
    ], { gap: 9 }),
    leaf("es-a-web", "airduct-web", "[NO REPO]", "LayoutDashboard", "gate"),
    leaf("es-a-db", "airduct-db :5432", "[pgvector]", "Database"),
  ], { gap: 12 });

  // ── Boiling, and our own machine ───────────────────────────────────────────
  // Boiling is deliberately hollow. Drawing plausible contents would make the
  // diagram wrong in the one way a reader could never detect.
  const rest = box("es-rest", undefined, "col", [
    box("es-boiling", "BOILING · AWS", "col", [
      box("es-b-vendor", "omnigent [vendor]", "col", [
        leaf("es-b-caddy", "caddy :443", undefined, "Network"),
        leaf("es-b-omni", "omnigent", "[:latest \u2014 floats]", "Boxes", "gate"),
        leaf("es-b-pg", "postgres :5432", undefined, "Database"),
      ], { gap: 9 }),
      box("es-b-ours", "ours", "col", [
        leaf("es-b-app", "boiling-app", "[API + MCP]", "Server"),
        leaf("es-b-tg", "omni-boiling", "[Telegram]", "Send"),
      ], { gap: 9 }),
      leaf("es-b-web", "boiling-web", "[NO REPO]", "LayoutDashboard", "gate"),
      leaf("es-b-db", "boiling-db", "[postgres 17 · no pgvector]", "Database"),
    ], { gap: 9 }),
    box("es-tm", "TAILORMIND · Hetzner", "col", [
      leaf("es-tm-desk", "the desk", "[Jarvis + 9 heads]", "LayoutGrid"),
      leaf("es-tm-tg", "telegram", "[7 bots]", "Send"),
      leaf("es-tm-voice", "voice :8140", "[STT + Kokoro]", "Mic"),
    ], { gap: 9 }),
  ], { pad: 0, gap: 22 });

  return {
    key: "estate",
    title: "THE ESTATE — every machine, and where its code comes from",
    subtitle:
      "Read off the running machines, 12–13 Aug 2026 · a wire means the code is traceable to a repository · red means it is not",
    // GITHUB sits in the MIDDLE, not on the left, and that is a rendering
    // decision rather than a compositional one. c4.tsx paints the wire SVG
    // first and the boxes over it, so a wire that crosses a container is
    // swallowed by that container's fill — the failure this skill documents,
    // and the one the first version of this diagram shipped with: the Outform
    // wires ran behind the Falcon column and a "git" label was clipped to "t".
    // With the repositories between the two clients they feed, every wire
    // reaches an ADJACENT column and nothing has to cross anything.
    cols: [
      { root: falcon, align: "top" },
      { root: repos, align: "center" },
      { root: outform, align: "top" },
      { root: airduct, align: "top" },
      { root: rest, align: "top" },
    ],
    wires: [
      // Back ends — the layer that already made it into git.
      { a: "es-r-fal", b: "es-f-app", as: "l", bs: "r", label: "git" },
      { a: "es-r-out", b: "es-o-app", as: "r", bs: "l", label: "git" },

      // Front ends — the layer that never did. Both of these are a build
      // copied off a laptop, which is why they are dashed and red: the wire
      // exists in practice but not in a form anyone can reproduce.
      { a: "es-t-fal", b: "es-f-web", as: "l", bs: "r", label: "rsync", red: true, dashed: true },
      { a: "es-t-out", b: "es-o-web", as: "r", bs: "l", label: "rsync", red: true, dashed: true },
    ],
    legend: [
      { style: "solid", label: "git — the box builds from a repository" },
      { style: "dash", label: "rsync — a build copied from a laptop; no repository behind it" },
      { style: "red", label: "no repository at all · or not verified" },
    ],
    padBottom: 62,
  };
})();

/** One picture of the whole estate: every machine, and the repo behind it. */
export function TailormindEstateDiagram() {
  return <Canvas d={ESTATE} mode="contrast" />;
}
